import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { Footer } from "@/components/utilities/Footer";
import { useTheme } from "@/contexts/ThemeContext";

const PrivacyPolicy = () => {
  const { theme } = useTheme();

  // Force scroll to top on component mount with a more reliable approach
  useEffect(() => {
    // Use both approaches for maximum compatibility
    window.scrollTo(0, 0);
    // Also try this alternative method
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    
    // Do this with a slight delay to ensure it happens after any layout calculations
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`min-h-screen flex flex-col ${theme === 'dark' ? 'bg-[#030405]' : 'bg-background'}`}>
      <div className="container mx-auto px-4 py-16 flex-grow">
        <div className="mb-8">
          <Button
            variant="ghost"
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            asChild
          >
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
        
        <div className={`max-w-4xl mx-auto ${theme === 'dark' ? 'bg-[#050607]/70' : 'bg-white/80'} rounded-lg p-8 shadow-lg border ${theme === 'dark' ? 'border-gray-800/50' : 'border-gray-200'}`}>
          <h1 className={`text-3xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>Privacy Policy</h1>
          
          <div className={`prose ${theme === 'dark' ? 'prose-invert' : ''} max-w-none`}>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>Last Updated: {new Date().toLocaleDateString()}</p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>1. Introduction</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Welcome to DocGen. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
            </p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>2. Information We Collect</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              We collect information that you provide directly to us, such as when you create an account, use our services, or contact us. This may include:
            </p>
            <ul className={`list-disc pl-6 mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>Personal information such as your name and email address</li>
              <li>Account credentials</li>
              <li>Information about your repositories when you connect with GitHub</li>
              <li>User-generated content and preferences</li>
            </ul>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>3. How We Use Your Information</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              We use the information we collect to:
            </p>
            <ul className={`list-disc pl-6 mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Send administrative messages, updates, and security alerts</li>
              <li>Respond to your comments and questions</li>
              <li>Monitor and analyze trends and usage</li>
            </ul>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>4. Data Sharing and Disclosure</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              We may share your information in the following circumstances:
            </p>
            <ul className={`list-disc pl-6 mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>With service providers who perform services on our behalf</li>
              <li>To comply with legal obligations</li>
              <li>To protect the rights, property, or safety of our users or others</li>
              <li>In connection with a business transaction such as a merger or acquisition</li>
            </ul>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>5. Data Security</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              We implement appropriate technical and organizational measures to protect your personal information from unauthorized access, loss, or disclosure.
            </p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>6. User Rights</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className={`list-disc pl-6 mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>The right to access your personal information</li>
              <li>The right to correct inaccurate or incomplete information</li>
              <li>The right to delete your personal information</li>
              <li>The right to restrict or object to processing</li>
            </ul>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>7. Changes to This Policy</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>8. Contact Us</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              If you have any questions about this Privacy Policy, please contact us at <a href="mailto:aniket@docgen.dev" className={`${theme === 'dark' ? 'text-primary' : 'text-primary-light'} hover:underline`}>aniket@docgen.dev</a>.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy; 